import { useQuery } from "@tanstack/react-query";
import type { Dayjs } from "dayjs";
import { apiGet } from "../api";

// Certificate e-mails are sent through letterman, there and then: reception's
// "Uhrazeno" and the e-shop sync send the delivery e-mail and fail when
// letterman does not take it — unless it refuses the e-mail for good, which
// no retry could change: the payment is then recorded with a warning and only
// a manager's resend can still deliver. Nothing is ever waiting in kartis. What
// happened to an e-mail afterwards — attempts, SMTP replies, the delivery
// timeline — is letterman's to show; kartis only lists the certificate's
// e-mails as letterman has them (its API reads them, so the letterman API key
// never reaches the browser).

/** Delivery state of a letterman message. */
export type EmailState = "pending" | "leased" | "sent" | "failed" | "cancelled" | "suppressed" | "expired";

/** One of a certificate's e-mails, as letterman has it. */
export interface CertificateEmail {
  /** The letterman message id. */
  id: string;
  to: string;
  subject: string;
  state: EmailState;
  created_at: Dayjs;
  sent_at: Dayjs | null;
  /** The message in letterman's admin UI. */
  letterman_url: string;
}

export interface CertificateEmailsResponse {
  items: CertificateEmail[];
}

/** An e-mail letterman accepted. */
export interface SentEmailResponse {
  sent: boolean;
  message_id: string;
  to: string;
  /** Letterman's state of the message: `pending` for a fresh one, whatever became of the first attempt for a replay. */
  state: EmailState;
  /** Letterman already had this e-mail and keeps its own recipient, so `to` is the address kartis asked for. */
  replayed: boolean;
}

/**
 * What to tell the user after letterman accepted an e-mail. A replay is an
 * e-mail letterman already had, which may long since have been sent — or have
 * failed: it says so instead of promising delivery, and points at the list.
 */
export function sentEmailNotice(sent: SentEmailResponse): string {
  if (!sent.replayed) {
    return `E-mail s certifikátem byl odeslán na ${sent.to}.`;
  }
  if (sent.state === "pending" || sent.state === "leased") {
    return `Letterman už e-mail s certifikátem měl z dřívějšího pokusu a doručuje ho.`;
  }
  const state = EMAIL_STATE_LABELS[sent.state] ?? sent.state;
  return `Letterman už e-mail s certifikátem měl z dřívějšího pokusu — jeho stav je „${state}", podrobnosti jsou v seznamu e-mailů.`;
}

/** Letterman's message states, in Czech. */
export const EMAIL_STATE_LABELS: Record<string, string> = {
  pending: "Čeká",
  leased: "Odesílá se",
  sent: "Odesláno",
  failed: "Selhalo",
  cancelled: "Zrušeno",
  suppressed: "Blokováno",
  expired: "Vypršelo",
};

/**
 * A certificate's e-mails, read from letterman. Nothing polls: an e-mail is
 * sent while the user waits, and sending one invalidates ["emails"].
 */
export function useCertificateEmails(certificateId: number) {
  return useQuery<CertificateEmailsResponse>({
    queryKey: ["emails", "certificate", certificateId],
    queryFn: () => apiGet(`/certificates/${certificateId}/emails`),
  });
}

/** Why a certificate's e-mail did not arrive. */
export type EmailProblemReason = "refused" | "blocked" | "failed";

export interface EmailProblem {
  certificate_id: number;
  redemption_code: string;
  customer_name: string;
  customer_email: string;
  /** The address that failed — not always the customer's, since a resend may have gone elsewhere. */
  to: string;
  reason: EmailProblemReason;
  /** Letterman's own words about it, when there are any. */
  detail?: string;
  message_id?: string;
  at: string;
}

export interface EmailProblemsResponse {
  items: EmailProblem[];
  /** Letterman could not be read, so the list may be short — it must not read as good news. */
  letterman_unavailable: boolean;
}

export const EMAIL_PROBLEM_LABELS: Record<EmailProblemReason, string> = {
  refused: "Letterman e-mail nepřevzal",
  blocked: "Adresa je blokovaná",
  failed: "Nepodařilo se doručit",
};

/**
 * The certificates whose e-mail did not arrive.
 *
 * Refreshed when the page is opened and on demand: nothing here changes on
 * its own — a row leaves the list when a person resends the certificate.
 */
export function useEmailProblems() {
  return useQuery<EmailProblemsResponse>({
    queryKey: ["emails", "problems"],
    queryFn: () => apiGet(`/certificates/email-problems`),
  });
}
